import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  FormHelperText,
  Alert,
  alpha,
  useTheme,
} from '@mui/material';
import { Business, TrendingUp, Public } from '@mui/icons-material';

interface CompanyProfileStepProps {
  onNext: (data: any) => void;
  onBack: () => void;
  data: any;
  updateData: (data: any) => void;
}

const industries = [
  'Electronics & Technology',
  'Automotive',
  'Pharmaceuticals',
  'Food & Beverage',
  'Retail & E-commerce',
  'Manufacturing',
  'Healthcare',
  'Fashion & Apparel',
  'Construction',
  'Energy',
  'Other',
];

const companySizes = [
  '1-10 employees',
  '11-50 employees',
  '51-200 employees',
  '201-500 employees',
  '501-1000 employees',
  '1000+ employees',
];

const regions = [
  'United States',
  'Europe',
  'North America',
  'Asia Pacific',
  'Middle East',
  'Africa',
  'Latin America',
  'Global',
];

const businessGoals = [
  'Reduce inventory costs',
  'Improve forecast accuracy',
  'Minimize stockouts',
  'Optimize supplier relationships',
  'Reduce lead times',
  'Improve sustainability',
  'Increase supply chain visibility',
  'Enhance risk management',
  'Reduce operational costs',
  'Improve customer satisfaction',
];

const CompanyProfileStep: React.FC<CompanyProfileStepProps> = ({
  onNext,
  data,
  updateData,
}) => {
  const theme = useTheme();
  const [formData, setFormData] = useState({
    companyName: data.companyName || '',
    industry: data.industry || '',
    companySize: data.companySize || '',
    operatingRegions: data.operatingRegions || [],
    primaryGoals: data.primaryGoals || [],
    annualRevenue: data.annualRevenue || '',
    description: data.description || '',
  });

  const [errors, setErrors] = useState<any>({});

  const handleChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    updateData({ ...formData, [field]: value });
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev: any) => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: any = {};
    
    if (!formData.companyName.trim()) {
      newErrors.companyName = 'Company name is required';
    }
    if (!formData.industry) {
      newErrors.industry = 'Please select your industry';
    }
    if (!formData.companySize) {
      newErrors.companySize = 'Please select company size';
    }
    if (formData.operatingRegions.length === 0) {
      newErrors.operatingRegions = 'Please select at least one region';
    }
    if (formData.primaryGoals.length === 0) {
      newErrors.primaryGoals = 'Please select at least one goal';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateForm()) {
      onNext(formData);
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            color: theme.palette.primary.main,
            mx: 'auto',
            mb: 2,
          }}
        >
          <Business sx={{ fontSize: 32 }} />
        </Box>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
          Tell Us About Your Business
        </Typography>
        <Typography variant="body1" color="text.secondary">
          This helps us customize ASCIN to your specific needs
        </Typography>
      </Box>

      {/* Form */}
      <Grid container spacing={3}>
        {/* Company Name */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Company Name"
            value={formData.companyName}
            onChange={(e) => handleChange('companyName', e.target.value)}
            error={!!errors.companyName}
            helperText={errors.companyName}
            placeholder="e.g., Acme Electronics Inc"
            required
          />
        </Grid>

        {/* Industry */}
        <Grid item xs={12} md={6}>
          <FormControl fullWidth error={!!errors.industry} required>
            <InputLabel>Industry</InputLabel>
            <Select
              value={formData.industry}
              onChange={(e) => handleChange('industry', e.target.value)}
              label="Industry"
            >
              {industries.map((industry) => (
                <MenuItem key={industry} value={industry}>
                  {industry}
                </MenuItem>
              ))}
            </Select>
            {errors.industry && (
              <FormHelperText>{errors.industry}</FormHelperText>
            )}
          </FormControl>
        </Grid>

        {/* Company Size */}
        <Grid item xs={12} md={6}>
          <FormControl fullWidth error={!!errors.companySize} required>
            <InputLabel>Company Size</InputLabel>
            <Select
              value={formData.companySize}
              onChange={(e) => handleChange('companySize', e.target.value)}
              label="Company Size"
            >
              {companySizes.map((size) => (
                <MenuItem key={size} value={size}>
                  {size}
                </MenuItem>
              ))}
            </Select>
            {errors.companySize && (
              <FormHelperText>{errors.companySize}</FormHelperText>
            )}
          </FormControl>
        </Grid>

        {/* Operating Regions */}
        <Grid item xs={12}>
          <FormControl fullWidth error={!!errors.operatingRegions} required>
            <InputLabel>Operating Regions</InputLabel>
            <Select
              multiple
              value={formData.operatingRegions}
              onChange={(e) => handleChange('operatingRegions', e.target.value)}
              input={<OutlinedInput label="Operating Regions" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected as string[]).map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
            >
              {regions.map((region) => (
                <MenuItem key={region} value={region}>
                  {region}
                </MenuItem>
              ))}
            </Select>
            {errors.operatingRegions && (
              <FormHelperText>{errors.operatingRegions}</FormHelperText>
            )}
            <FormHelperText>
              Select all regions where you operate
            </FormHelperText>
          </FormControl>
        </Grid>

        {/* Annual Revenue (Optional) */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Annual Revenue (Optional)"
            value={formData.annualRevenue}
            onChange={(e) => handleChange('annualRevenue', e.target.value)}
            placeholder="e.g., $5M - $10M"
            helperText="This helps us provide relevant benchmarks"
          />
        </Grid>

        {/* Primary Goals */}
        <Grid item xs={12}>
          <FormControl fullWidth error={!!errors.primaryGoals} required>
            <InputLabel>Primary Business Goals</InputLabel>
            <Select
              multiple
              value={formData.primaryGoals}
              onChange={(e) => handleChange('primaryGoals', e.target.value)}
              input={<OutlinedInput label="Primary Business Goals" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected as string[]).map((value) => (
                    <Chip
                      key={value}
                      label={value}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                </Box>
              )}
            >
              {businessGoals.map((goal) => (
                <MenuItem key={goal} value={goal}>
                  {goal}
                </MenuItem>
              ))}
            </Select>
            {errors.primaryGoals && (
              <FormHelperText>{errors.primaryGoals}</FormHelperText>
            )}
            <FormHelperText>
              Select your top 3-5 goals (we'll prioritize these)
            </FormHelperText>
          </FormControl>
        </Grid>

        {/* Description */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Brief Description (Optional)"
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            placeholder="Tell us more about your business, products, and supply chain challenges..."
            helperText="This helps our AI agents provide more relevant recommendations"
          />
        </Grid>
      </Grid>

      {/* Info Cards */}
      <Grid container spacing={2} sx={{ mt: 3 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ bgcolor: alpha(theme.palette.info.main, 0.05) }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Public sx={{ mr: 1, color: theme.palette.info.main }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Why We Ask
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Your company profile helps us configure AI agents with
                industry-specific knowledge and tailor recommendations to your
                business size and goals.
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ bgcolor: alpha(theme.palette.success.main, 0.05) }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <TrendingUp sx={{ mr: 1, color: theme.palette.success.main }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  What You'll Get
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Personalized dashboards, industry benchmarks, goal-specific KPIs,
                and AI recommendations aligned with your business objectives.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Privacy Notice */}
      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="body2">
          🔒 <strong>Your data is secure:</strong> All information is encrypted
          and used only to customize your ASCIN experience. You can update or
          delete this information anytime from your settings.
        </Typography>
      </Alert>

      {/* Hidden submit button that parent can trigger */}
      <Box sx={{ display: 'none' }}>
        <button onClick={handleNext}>Submit</button>
      </Box>
    </Box>
  );
};

export default CompanyProfileStep;
